/**
 * Generator Runner
 * UI for running the AI pick generator with selected configs
 */
import { type ReactElement, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  type ConfigToggles,
  type GeneratorConfig,
  type GeneratorResult,
  useGenerator,
} from '../hooks/useGenerator';

// =============================================================================
// Toggle Display Helpers
// =============================================================================

const TOGGLE_LABELS: Record<keyof ConfigToggles, { label: string; category: string }> = {
  l5Average: { label: 'L5 Avg', category: 'Stats' },
  l10Average: { label: 'L10 Avg', category: 'Stats' },
  l20Average: { label: 'L20 Avg', category: 'Stats' },
  seasonAverage: { label: 'Season Avg', category: 'Stats' },
  blowoutRisk: { label: 'Blowout Risk', category: 'Game' },
  backToBack: { label: 'Back-to-Back', category: 'Game' },
  paceImpact: { label: 'Pace Impact', category: 'Game' },
  gameTotal: { label: 'Game Total', category: 'Game' },
  trendAnalysis: { label: 'Trends', category: 'Other' },
  injuryStatus: { label: 'Injuries', category: 'Other' },
  playerVsTeamHistory: { label: 'H2H History', category: 'Matchup' },
  playerPosition: { label: 'Position', category: 'Matchup' },
  defVsPosition: { label: 'Shot Location Defense', category: 'Defense' },
};

// =============================================================================
// Helper Components
// =============================================================================

function ConfigPopover({
  config,
  anchorEl,
}: {
  config: GeneratorConfig;
  anchorEl: HTMLElement | null;
}): ReactElement | null {
  if (!anchorEl) return null;

  const toggles = config.toggles;
  const rect = anchorEl.getBoundingClientRect();

  // Calculate if popover should appear above or below
  const popoverHeight = 280; // Approximate max height of popover
  const spaceBelow = window.innerHeight - rect.bottom;
  const showAbove = spaceBelow < popoverHeight && rect.top > popoverHeight;

  // Calculate horizontal position (ensure it doesn't overflow right edge)
  const popoverWidth = 256; // w-64 = 16rem = 256px
  let left = rect.left;
  if (left + popoverWidth > window.innerWidth - 16) {
    left = window.innerWidth - popoverWidth - 16;
  }

  const positionStyle = showAbove
    ? { bottom: window.innerHeight - rect.top + 8, left }
    : { top: rect.bottom + 8, left };

  // Handle case where toggles haven't loaded yet (stale cache)
  if (!toggles) {
    return (
      <div
        className="fixed z-[9999] bg-theme-surface border border-theme-border rounded-lg shadow-xl p-3 text-xs w-64 pointer-events-none"
        style={positionStyle}
      >
        <div className="font-medium text-gray-200 mb-2">{config.name}</div>
        <div className="text-theme-text-tertiary">Loading config details...</div>
      </div>
    );
  }

  const enabledToggles = (Object.keys(toggles) as Array<keyof ConfigToggles>).filter(
    (k) => toggles[k],
  );
  const disabledToggles = (Object.keys(toggles) as Array<keyof ConfigToggles>).filter(
    (k) => !toggles[k],
  );

  // Group by category
  const groupByCategory = (keys: Array<keyof ConfigToggles>) => {
    return keys.reduce(
      (acc, key) => {
        const cat = TOGGLE_LABELS[key].category;
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(key);
        return acc;
      },
      {} as Record<string, Array<keyof ConfigToggles>>,
    );
  };

  const enabledByCategory = groupByCategory(enabledToggles);
  const disabledByCategory = groupByCategory(disabledToggles);

  return (
    <div
      className="fixed z-[9999] bg-theme-surface border border-theme-border rounded-lg shadow-xl p-3 text-xs w-64 pointer-events-none"
      style={positionStyle}
    >
      <div className="font-medium text-gray-200 mb-2 pb-1 border-b border-theme-border">
        {config.name}
      </div>

      {/* Prop Types */}
      <div className="mb-2 pb-2 border-b border-theme-border">
        <div className="text-theme-text-tertiary text-[10px] uppercase mb-1">Prop Types</div>
        <div className="flex flex-wrap gap-1">
          {config.propTypes && config.propTypes.length > 0 ? (
            config.propTypes.map((pt) => (
              <span
                key={pt}
                className="px-1.5 py-0.5 bg-theme-primary/50 text-blue-300 rounded text-[10px]"
              >
                {pt.replace('player_', '').replace(/_/g, ' ')}
              </span>
            ))
          ) : (
            <span className="text-theme-text-secondary text-[10px]">All prop types</span>
          )}
        </div>
      </div>

      {/* Enabled Toggles */}
      {enabledToggles.length > 0 && (
        <div className="mb-2">
          <div className="text-theme-success text-[10px] uppercase mb-1 flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-theme-success rounded-full" />
            Enabled
          </div>
          <div className="space-y-1">
            {Object.entries(enabledByCategory).map(([category, keys]) => (
              <div key={category} className="flex flex-wrap gap-1">
                <span className="text-theme-text-tertiary text-[10px] w-12">{category}:</span>
                {keys.map((key) => (
                  <span key={key} className="text-theme-text-secondary text-[10px]">
                    {TOGGLE_LABELS[key].label}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disabled Toggles */}
      {disabledToggles.length > 0 && (
        <div>
          <div className="text-theme-danger text-[10px] uppercase mb-1 flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-theme-danger rounded-full" />
            Disabled
          </div>
          <div className="space-y-1">
            {Object.entries(disabledByCategory).map(([category, keys]) => (
              <div key={category} className="flex flex-wrap gap-1">
                <span className="text-theme-text-tertiary text-[10px] w-12">{category}:</span>
                {keys.map((key) => (
                  <span key={key} className="text-theme-text-tertiary text-[10px] line-through">
                    {TOGGLE_LABELS[key].label}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ConfigButton({
  config,
  selected,
  disabled,
  onClick,
}: {
  config: GeneratorConfig;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}): ReactElement {
  const [hovered, setHovered] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Only show popover if toggles are loaded
  const canShowPopover = hovered && config.toggles != null;

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        onClick={onClick}
        disabled={disabled}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`px-2 py-1 text-xs rounded transition-colors ${
          selected
            ? 'bg-theme-primary-fill text-theme-on-primary-fill'
            : 'bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-surface-hover'
        }`}
      >
        {config.name} ({config.gameCount === 'all' ? 'all' : config.gameCount}g)
      </button>
      {canShowPopover &&
        createPortal(<ConfigPopover config={config} anchorEl={buttonRef.current} />, document.body)}
    </>
  );
}

function GamesList({
  games,
  loading,
}: {
  games: Array<{
    id: string;
    homeTeam: string;
    awayTeam: string;
    gameStartsAt: string;
  }>;
  loading: boolean;
}): ReactElement {
  if (loading) {
    return <div className="text-theme-text-secondary text-sm">Loading games...</div>;
  }

  if (games.length === 0) {
    return <div className="text-theme-warning text-sm">No upcoming games available</div>;
  }

  return (
    <div className="space-y-1">
      <div className="text-xs text-theme-text-tertiary uppercase">
        Today's Games ({games.length})
      </div>
      <div className="grid grid-cols-1 @2xl:grid-cols-2 gap-1 text-sm">
        {games.map((game) => {
          const time = new Date(game.gameStartsAt).toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
          });
          return (
            <div key={game.id} className="text-theme-text-secondary text-xs">
              {game.awayTeam} @ {game.homeTeam} ({time})
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatGeneratedAt(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatGameDateTime(isoString: string): string {
  const date = new Date(isoString);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();

  if (isToday) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface GeneratedParlay {
  name: string;
  legs: Array<{
    playerName: string;
    propType: string;
    line: number;
    direction: string;
    team?: string;
    opponentTeam?: string;
    homeTeam?: string;
    awayTeam?: string;
    gameStartsAt?: string;
  }>;
}

function ParlayCard({ parlay }: { parlay: GeneratedParlay }): ReactElement {
  return (
    <div className="rounded text-xs bg-theme-surface/50 p-2">
      <div className="font-medium text-theme-text-secondary mb-1">{parlay.name}</div>
      <div className="space-y-0.5">
        {parlay.legs.map((leg, legIdx) => (
          <div key={legIdx} className="text-theme-text-secondary">
            <span>
              {leg.playerName} {leg.direction} {leg.line} {leg.propType}
            </span>
            {leg.awayTeam && leg.homeTeam && (
              <span className="text-theme-text-tertiary ml-1">
                ({leg.awayTeam} @ {leg.homeTeam}
                {leg.gameStartsAt && ` • ${formatGameDateTime(leg.gameStartsAt)}`})
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ResultCard({ result }: { result: GeneratorResult }): ReactElement {
  const durationSec = (result.durationMs / 1000).toFixed(1);

  return (
    <div
      className={`p-3 rounded-lg ${result.success ? 'bg-theme-surface-hover/50' : 'bg-theme-danger/30'}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="font-medium text-sm text-gray-200">{result.configName}</div>
          {result.generatedAt && (
            <span className="text-xs text-theme-text-tertiary">
              {formatGeneratedAt(result.generatedAt)}
            </span>
          )}
        </div>
        <div className={`text-xs ${result.success ? 'text-theme-success' : 'text-theme-danger'}`}>
          {result.success ? 'Success' : 'Failed'}
        </div>
      </div>

      {result.success ? (
        <>
          <div className="grid grid-cols-3 gap-2 text-xs mb-2">
            <div>
              <span className="text-theme-text-tertiary">Parlays:</span>{' '}
              <span className="text-theme-text-secondary">{result.parlays}</span>
            </div>
            <div>
              <span className="text-theme-text-tertiary">Legs:</span>{' '}
              <span className="text-theme-text-secondary">{result.legs}</span>
            </div>
            <div>
              <span className="text-theme-text-tertiary">Time:</span>{' '}
              <span className="text-theme-text-secondary">{durationSec}s</span>
            </div>
          </div>

          {Object.keys(result.tiers).length > 0 && (
            <div className="text-xs text-theme-text-tertiary mb-2">
              Tiers:{' '}
              {Object.entries(result.tiers)
                .map(([tier, count]) => `${tier}: ${count}`)
                .join(', ')}
            </div>
          )}

          {result.tokenUsage && (
            <div className="text-xs text-theme-text-tertiary">
              Tokens: {result.tokenUsage.inputTokens.toLocaleString()} in /{' '}
              {result.tokenUsage.outputTokens.toLocaleString()} out
            </div>
          )}

          {/* Show picks preview */}
          {result.picks && result.picks.length > 0 && (
            <div className="mt-3 pt-3 border-t border-theme-border">
              <div className="text-xs text-theme-text-tertiary mb-2">Generated Picks:</div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {result.picks.map((parlay, idx) => (
                  <ParlayCard key={idx} parlay={parlay} />
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-xs text-red-300">{result.error || 'Unknown error'}</div>
      )}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function GeneratorRunner(): ReactElement {
  const {
    configs: _configs,
    categories,
    loadingConfigs,
    games,
    loadingGames,
    selectedConfig,
    setSelectedConfig,
    analysisMode,
    setAnalysisMode,
    running,
    results,
    loopCount,
    setLoopCount,
    loopRunning,
    loopProgress,
    stopLoop,
    runGenerator,
    runGeneratorLoop,
    clearResults,
    clearErrors,
    refreshGames,
    resetState,
  } = useGenerator();

  // Check if there are any errors in the results
  const hasErrors = useMemo(() => results.some((r) => !r.success), [results]);

  return (
    <div className="bg-theme-surface/50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-theme-text-secondary">Generator Runner</h3>
        <div className="flex items-center gap-3">
          {(running || loopRunning) && (
            <button
              type="button"
              onClick={resetState}
              className="text-xs text-theme-warning hover:text-theme-warning transition-colors"
            >
              Force Reset
            </button>
          )}
          {hasErrors && (
            <button
              type="button"
              onClick={clearErrors}
              className="text-xs text-theme-danger hover:text-red-300 transition-colors"
            >
              Clear Errors
            </button>
          )}
          {results.length > 0 && (
            <button
              type="button"
              onClick={clearResults}
              className="text-xs text-theme-text-tertiary hover:text-theme-text-secondary transition-colors"
            >
              Clear Results
            </button>
          )}
        </div>
      </div>

      {/* Games Info */}
      <div className="mb-4 p-3 bg-theme-surface/50 rounded">
        <div className="flex items-center justify-between mb-2">
          <GamesList games={games} loading={loadingGames} />
          <button
            type="button"
            onClick={refreshGames}
            disabled={loadingGames}
            className="text-xs text-theme-primary hover:text-blue-300"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Config Selection */}
      <div className="mb-4">
        <label className="text-xs text-theme-text-tertiary uppercase block mb-2">
          Select Config
        </label>
        {loadingConfigs || Object.keys(categories).length === 0 ? (
          <div className="text-theme-text-secondary text-sm">Loading configs...</div>
        ) : (
          <div className="space-y-3">
            {Object.entries(categories).map(([category, categoryConfigs]) => (
              <div key={category}>
                <div className="text-xs text-theme-text-tertiary mb-1 capitalize">{category}</div>
                <div className="flex flex-wrap gap-2">
                  {categoryConfigs.map((config) => (
                    <ConfigButton
                      key={config.name}
                      config={config}
                      selected={selectedConfig === config.name}
                      disabled={running}
                      onClick={() => setSelectedConfig(config.name)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Analysis Mode */}
      <div className="mb-4">
        <label className="text-xs text-theme-text-tertiary uppercase block mb-2">
          Analysis Mode
        </label>
        <div className="flex gap-2">
          {(
            [
              { value: 'standard', label: 'Standard' },
              { value: 'detailed', label: 'Detailed' },
              { value: 'premium', label: 'Premium' },
            ] as const
          ).map((option) => (
            <button
              type="button"
              key={option.value}
              onClick={() => setAnalysisMode(option.value)}
              disabled={running}
              className={`px-3 py-1.5 text-xs rounded transition-colors ${
                analysisMode === option.value
                  ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                  : 'bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-surface-hover'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Run Buttons */}
      <div className="mb-4 space-y-3">
        <div className="flex items-center gap-3">
          {/* Single Run Button */}
          <button
            type="button"
            onClick={runGenerator}
            disabled={!selectedConfig || running || loopRunning || games.length === 0}
            className="flex-1 px-4 py-2 bg-theme-primary-fill text-theme-on-primary-fill rounded font-medium hover:bg-theme-primary-fill transition-colors"
          >
            {running && !loopRunning ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Running...
              </span>
            ) : (
              `Run ${selectedConfig || 'Generator'}`
            )}
          </button>

          {/* Loop Controls - grouped together with border */}
          <div className="flex items-center gap-2 bg-theme-surface/50 rounded-lg p-1 border border-theme-border">
            {loopRunning ? (
              <button
                type="button"
                onClick={stopLoop}
                className="px-3 py-1.5 bg-theme-danger text-white rounded font-medium text-sm hover:bg-theme-danger transition-colors"
              >
                <span className="flex items-center justify-center gap-1.5">
                  <span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
                  Stop ({loopProgress?.current}/{loopProgress?.total})
                </span>
              </button>
            ) : (
              <button
                type="button"
                onClick={runGeneratorLoop}
                disabled={!selectedConfig || running || games.length === 0 || loopCount < 1}
                className="px-3 py-1.5 bg-theme-success text-white rounded font-medium text-sm hover:bg-theme-success transition-colors"
              >
                Loop
              </button>
            )}
            <input
              type="number"
              min={1}
              max={50}
              value={loopCount}
              onChange={(e) =>
                setLoopCount(Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 1)))
              }
              disabled={loopRunning}
              className="w-12 px-2 py-1.5 bg-theme-surface-hover text-gray-200 text-sm rounded border border-theme-border focus:border-theme-success focus:outline-none text-center"
            />
          </div>
        </div>

        {games.length === 0 && !loadingGames && (
          <div className="text-xs text-theme-warning text-center">
            No games available to generate picks for
          </div>
        )}
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs text-theme-text-tertiary uppercase">
            Results ({results.length})
          </div>
          {results.map((result, idx) => (
            <ResultCard key={`${result.configName}-${idx}`} result={result} />
          ))}
        </div>
      )}
    </div>
  );
}
